import React, { useMemo } from "react"
import ConditionalRender from "../../../ConditionalRender.js"
import { Icons } from "../Icons/index.js"

/**
 * Size → CSS class mapping for textarea.
 */
const InputAreaSizes = {
    Small: "small",
    Medium: "medium",
    Large: "large",
}

const InputAreaSizeFonts = {
    Small: "body-s-semibold",
    Medium: "body-s-semibold",
    Large: "body-m-semibold",
}

/**
 * InputArea — Multi-line text input (textarea).
 *
 * Same pattern as InputBox but renders a <textarea> instead of <input>.
 * Supports the same wrapper mode: pass a child <textarea> to clone it
 * with merged props, or let InputArea render its own.
 *
 * @param {string}  Size           - 'Small' | 'Medium' | 'Large'
 * @param {string}  label          - Label text above the textarea
 * @param {number}  rows           - Number of visible text rows (default 4)
 * @param {string}  error          - Error message (triggers error styling)
 * @param {boolean} disabled       - Disabled state
 * @param {boolean} resizable      - Drag the corner to resize vertically (size height acts as the minimum)
 * @param {func}    handleOnChange - Change handler
 */
export const InputArea = ({
    children,
    Size = "Medium",
    label = "",
    showLabel = false,
    labelClass = "input-box-label body-s-semibold",
    showHelperText = false,
    helperText = "",
    helperTextClass = "helper-text support-m",
    helperTextStyle = {},
    inputStyle = {},
    value,
    placeholder = "",
    rows = 4,
    customClass = "",
    customStyle = {},
    showIcon = false,
    IconName = null,
    placeholderClass = "",
    IconClass = "icon",
    IconStyle = {},
    handleOnChange,
    handleOnKeyDown,
    error = "",
    disabled = false,
    fontClass = null,
    containerClass = "",
    name,
    resizable = false,
}) => {

    const inputSize = InputAreaSizes[Size]

    const computed_classes = useMemo(() =>
        [
            "input-area",
            `input-area-${inputSize}`,
            fontClass ?? InputAreaSizeFonts[Size],
            error && "input-area-error",
            disabled && "input-area-disabled",
            resizable && "input-area--resizable",
            customClass,
            placeholderClass,
        ].filter(Boolean).join(" "),
        [inputSize, Size, fontClass, error, disabled, resizable, customClass, placeholderClass]
    );

    /** Renders the textarea — either standalone or by cloning a child element */
    const renderTextarea = () => {
        if (!children) {
            return (
                <textarea
                    className={computed_classes}
                    disabled={disabled}
                    value={value}
                    placeholder={placeholder}
                    rows={rows}
                    name={name}
                    onChange={handleOnChange}
                    onKeyDown={handleOnKeyDown}
                />
            )
        }

        if (React.isValidElement(children)) {
            return React.cloneElement(children, {
                className: [children.props.className, computed_classes].filter(Boolean).join(" "),
                disabled: children.props.disabled ?? disabled,
                value: children.props.value ?? value,
                placeholder: children.props.placeholder ?? placeholder,
                rows: children.props.rows ?? rows,
                onChange: children.props.onChange ?? handleOnChange,
                onKeyDown: children.props.onKeyDown ?? handleOnKeyDown,
            })
        }

        return children
    }

    return (
        <div className={containerClass} style={{ ...customStyle }}>
            <ConditionalRender if={showLabel && label !== ""}>
                <div className={labelClass}>{label}</div>
            </ConditionalRender>

            <div style={{ position: "relative", ...inputStyle }}>
                {renderTextarea()}

                {/* Optional floating icon inside the text area */}
                <ConditionalRender if={showIcon && !!IconName}>
                    <span className={`input-area__icon ${IconClass}`} style={IconStyle}>
                        <Icons name={IconName} size={24} />
                    </span>
                </ConditionalRender>
            </div>

            <ConditionalRender if={showHelperText && helperText !== ""}>
                <div className={helperTextClass} style={helperTextStyle}>
                    {helperText}
                </div>
            </ConditionalRender>
        </div>
    )
}